import { useState } from 'react';
import axios from 'axios';
import { Mail, MapPin, Phone, Upload, X } from 'lucide-react';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_FILE_SIZE_MB = 25;
const BYTES_PER_KB = 1024;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * BYTES_PER_KB * BYTES_PER_KB;

const getSubmitLabel = (uploading, submitting) => {
  if (uploading) return 'Uploading file...';
  if (submitting) return 'Submitting...';
  return 'Submit Inquiry';
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    industry: '',
    message: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        'application/pdf',
        'application/x-pdf',
        'image/jpeg',
        'image/png',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];
      
      if (!validTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.dwg')) {
        toast.error('Please upload a PDF, Image, Excel, or CAD file');
        return;
      }
      
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
        return;
      }
      
      setSelectedFile(file);
      toast.success('File selected');
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let fileId = null;

      // Upload file if selected
      if (selectedFile) {
        setUploading(true);
        const fileFormData = new FormData();
        fileFormData.append('file', selectedFile);

        const uploadResponse = await axios.post(`${API}/upload`, fileFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        fileId = uploadResponse.data.id;
        setUploading(false);
      }

      // Submit inquiry
      await axios.post(`${API}/inquiries`, {
        ...formData,
        file_id: fileId,
      });

      toast.success('Inquiry submitted successfully! We will contact you soon.');
      setFormData({
        name: '',
        company: '',
        email: '',
        industry: '',
        message: '',
      });
      setSelectedFile(null);
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="contact-hero-section"
        className="relative py-32 lg:py-40 overflow-hidden bg-omni-background-secondary"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-omni-accent mb-6">
            Get In Touch
          </div>
          <h1
            data-testid="contact-headline"
            className="text-5xl lg:text-7xl font-heading font-light tracking-tighter text-white mb-6 leading-[1.1] max-w-4xl"
          >
            Let&apos;s Discuss Your Material Requirements
          </h1>
          <p className="text-base lg:text-lg text-omni-text-secondary leading-relaxed max-w-3xl">
            Our technical team is ready to assist you with material sourcing, custom manufacturing
            quotes, or technical specifications. We respond to all inquiries within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section data-testid="contact-form-section" className="py-24 lg:py-32 bg-omni-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h3 className="text-2xl font-heading font-medium text-white mb-6">
                  Contact Information
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-omni-accent/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-omni-accent" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-omni-text-secondary mb-1">
                        Email
                      </div>
                      <a
                        href="mailto:info@omnimet.co.in"
                        className="text-white hover:text-omni-accent transition-colors block"
                      >
                        info@omnimet.co.in
                      </a>
                      <a
                        href="mailto:omnimet.info@gmail.com"
                        className="text-white hover:text-omni-accent transition-colors block"
                      >
                        omnimet.info@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-omni-accent/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-omni-accent" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-omni-text-secondary mb-1">
                        Phone
                      </div>
                      <a
                        href="tel:+919958584300"
                        className="text-white hover:text-omni-accent transition-colors"
                      >
                        +91 99585 84300
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-omni-accent/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-omni-accent" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-omni-text-secondary mb-1">
                        Corporate Office
                      </div>
                      <p className="text-white leading-relaxed">
                        Omnimet Private Limited
                        <br />
                        23, NIT 3, Faridabad,
                        <br />
                        Haryana - 121001, India
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-white/10">
                <h4 className="text-lg font-heading font-medium text-white mb-3">
                  Business Hours
                </h4>
                <p className="text-omni-text-secondary text-sm leading-relaxed">
                  Monday - Friday: 9:00 AM - 6:00 PM IST
                  <br />
                  Saturday: 9:00 AM - 1:00 PM IST
                  <br />
                  Sunday: Closed
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-omni-background-secondary border border-white/10 p-8 lg:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                <h3
                  data-testid="form-headline"
                  className="text-2xl font-heading font-medium text-white mb-8"
                >
                  Send us an Inquiry
                </h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-omni-text-secondary mb-2">
                        Your Name *
                      </label>
                      <input
                        data-testid="contact-name-input"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="bg-omni-background border border-white/10 focus:border-omni-accent focus:ring-1 focus:ring-omni-accent text-white p-4 w-full placeholder:text-omni-text-secondary outline-none"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-omni-text-secondary mb-2">
                        Company Name *
                      </label>
                      <input
                        data-testid="contact-company-input"
                        type="text"
                        required
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="bg-omni-background border border-white/10 focus:border-omni-accent focus:ring-1 focus:ring-omni-accent text-white p-4 w-full placeholder:text-omni-text-secondary outline-none"
                        placeholder="Your Company"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-omni-text-secondary mb-2">
                      Email Address *
                    </label>
                    <input
                      data-testid="contact-email-input"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-omni-background border border-white/10 focus:border-omni-accent focus:ring-1 focus:ring-omni-accent text-white p-4 w-full placeholder:text-omni-text-secondary outline-none"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-omni-text-secondary mb-2">
                      Industry Sector *
                    </label>
                    <select
                      data-testid="contact-industry-select"
                      required
                      value={formData.industry}
                      onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                      className="bg-omni-background border border-white/10 focus:border-omni-accent focus:ring-1 focus:ring-omni-accent text-white p-4 w-full outline-none"
                    >
                      <option value="">Select Industry</option>
                      <option value="Aerospace">Aerospace</option>
                      <option value="Automotive">Automotive</option>
                      <option value="Die & Mould">Die & Mould</option>
                      <option value="Defense">Defense</option>
                      <option value="Industrial">Industrial Manufacturing</option>
                      <option value="Marine">Marine</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-omni-text-secondary mb-2">
                      Message / Requirements *
                    </label>
                    <textarea
                      data-testid="contact-message-input"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="bg-omni-background border border-white/10 focus:border-omni-accent focus:ring-1 focus:ring-omni-accent text-white p-4 w-full placeholder:text-omni-text-secondary outline-none"
                      placeholder="Please provide details about your material requirements, quantities, specifications, or any questions..."
                    />
                  </div>

                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium text-omni-text-secondary mb-2">
                      Upload CAD / Technical Drawings (Optional)
                    </label>
                    <div className="relative">
                      {!selectedFile ? (
                        <label
                          data-testid="file-upload-label"
                          className="flex items-center justify-center gap-3 bg-omni-background border border-dashed border-white/20 p-8 cursor-pointer hover:border-omni-accent/50 transition-colors"
                        >
                          <Upload className="w-6 h-6 text-omni-accent" />
                          <span className="text-omni-text-secondary">
                            Click to upload PDF, CAD, or Image files (Max 25MB)
                          </span>
                          <input
                            data-testid="file-upload-input"
                            type="file"
                            onChange={handleFileChange}
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png,.dwg,.xls,.xlsx"
                          />
                        </label>
                      ) : (
                        <div
                          data-testid="file-selected-display"
                          className="flex items-center justify-between bg-omni-background border border-omni-accent/50 p-4"
                        >
                          <div className="flex items-center gap-3">
                            <Upload className="w-5 h-5 text-omni-accent" />
                            <span className="text-white text-sm">{selectedFile.name}</span>
                            <span className="text-omni-text-secondary text-xs">
                              ({(selectedFile.size / BYTES_PER_KB / BYTES_PER_KB).toFixed(2)} MB)
                            </span>
                          </div>
                          <button
                            type="button"
                            data-testid="file-remove-button"
                            onClick={removeFile}
                            className="text-omni-text-secondary hover:text-white transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-omni-text-secondary mt-2">
                      Supported formats: PDF, JPG, PNG, DWG, Excel
                    </p>
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={submitting || uploading}
                    className="w-full bg-omni-accent text-white px-8 py-4 hover:bg-omni-accent-hover transition-all font-medium disabled:opacity-50"
                  >
                    {getSubmitLabel(uploading, submitting)}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
